import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { purchaseOrdersAPI, vendorsAPI, departmentsAPI, budgetsAPI, glAccountsAPI } from '../api/client';

const EMPTY_LINE = { description: '', quantity: 1, unitPrice: '', glAccountId: '', budgetLineId: '', itemCategory: '' };

export default function PurchaseOrderCreate() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    poNumber: '',
    poType: 'standard',
    vendorId: '',
    departmentId: '',
    description: '',
    currency: 'GBP',
    poDate: new Date().toISOString().split('T')[0],
    expiryDate: '',
    notes: '',
  });

  const [lines, setLines] = useState([{ ...EMPTY_LINE }]);
  const [submitForApproval, setSubmitForApproval] = useState(false);
  const [autoSendOnApproval, setAutoSendOnApproval] = useState(false);
  const [vendorEmailCC, setVendorEmailCC] = useState('');
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringFrequency, setRecurringFrequency] = useState('monthly');
  const [recurringEndDate, setRecurringEndDate] = useState('');
  const [vendors, setVendors] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [budgetLines, setBudgetLines] = useState([]);
  const [allGlAccounts, setAllGlAccounts] = useState([]);
  const [deptGlAccounts, setDeptGlAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fieldError, setFieldError] = useState({});
  const [serverError, setServerError] = useState(null);
  const [poNumberLoading, setPoNumberLoading] = useState(false);

  const fetchNextPoNumber = async () => {
    setPoNumberLoading(true);
    try {
      const { data } = await purchaseOrdersAPI.getNextNumber();
      setForm(f => ({ ...f, poNumber: data.poNumber }));
      setFieldError(fe => ({ ...fe, poNumber: '' }));
    } catch (_) {}
    finally { setPoNumberLoading(false); }
  };

  useEffect(() => {
    const loadRef = async () => {
      try {
        const [vRes, dRes, glRes] = await Promise.all([
          vendorsAPI.getAll({ page: 1, pageSize: 999 }),
          departmentsAPI.getAll(),
          glAccountsAPI.getActive(),
        ]);
        setVendors((vRes.data?.vendors || vRes.data?.items || []).filter(v => v.ApprovalStatus === 'Approved'));
        setDepartments(dRes.data?.departments || dRes.data || []);
        setAllGlAccounts(glRes.data?.glAccounts || []);
      } catch (_) {}
    };
    loadRef();
    fetchNextPoNumber();
  }, []);

  // Load budget lines when department changes; derive GL accounts from them
  useEffect(() => {
    if (!form.departmentId) {
      setBudgetLines([]);
      setDeptGlAccounts([]);
      setLines(ls => ls.map(l => ({ ...l, glAccountId: '', budgetLineId: '' })));
      return;
    }
    budgetsAPI.getLinesByDepartment(form.departmentId)
      .then(r => {
        const bls = r.data?.lines || r.data || [];
        setBudgetLines(bls);
        const glMap = new Map();
        bls.forEach(bl => {
          if (bl.GLAccountID && !glMap.has(bl.GLAccountID)) {
            glMap.set(bl.GLAccountID, { GLAccountID: bl.GLAccountID, AccountCode: bl.AccountCode || '', AccountName: bl.AccountName || '' });
          }
        });
        setDeptGlAccounts([...glMap.values()]);
        setLines(ls => ls.map(l => ({ ...l, glAccountId: '', budgetLineId: '' })));
      })
      .catch(() => { setBudgetLines([]); setDeptGlAccounts([]); });
  }, [form.departmentId]);

  const setField = (key, val) => {
    setForm(f => ({ ...f, [key]: val }));
    setFieldError(fe => ({ ...fe, [key]: '' }));
  };

  const setLine = (i, key, val) => {
    setLines(ls => ls.map((l, idx) => idx === i ? { ...l, [key]: val } : l));
  };

  const handleLineGL = (i, glAccountId) => {
    setLines(ls => ls.map((l, idx) => idx === i ? { ...l, glAccountId, budgetLineId: '' } : l));
  };

  // GL accounts shown in dropdown: filtered by dept (via budget lines) if dept selected, else all
  const availableGlAccounts = form.departmentId
    ? (deptGlAccounts.length > 0 ? deptGlAccounts : allGlAccounts)
    : allGlAccounts;

  const addLine = () => setLines(ls => [...ls, { ...EMPTY_LINE }]);
  const removeLine = (i) => setLines(ls => ls.filter((_, idx) => idx !== i));

  const totalAmount = lines.reduce((s, l) => {
    const qty = parseFloat(l.quantity) || 0;
    const price = parseFloat(l.unitPrice) || 0;
    return s + qty * price;
  }, 0);

  const validate = () => {
    const errs = {};
    if (!form.poNumber.trim()) errs.poNumber = 'PO number is required';
    if (!form.vendorId) errs.vendorId = 'Vendor is required';
    if (submitForApproval && !form.departmentId) errs.departmentId = 'Department required to submit for approval';
    setFieldError(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setServerError(null);
    try {
      const payload = {
        ...form,
        vendorId: form.vendorId || null,
        departmentId: form.departmentId || null,
        poDate: form.poDate || null,
        expiryDate: form.expiryDate || null,
        lines: lines
          .filter(l => l.description || parseFloat(l.unitPrice) > 0)
          .map(l => ({
            description: l.description || null,
            quantity: parseFloat(l.quantity) || 1,
            unitPrice: parseFloat(l.unitPrice) || 0,
            glAccountId: l.glAccountId || null,
            budgetLineId: l.budgetLineId || null,
            itemCategory: l.itemCategory || null,
          })),
        submitForApproval,
        autoSendOnApproval,
        vendorEmailCC: vendorEmailCC.trim() || null,
        isRecurring,
        recurringFrequency: isRecurring ? recurringFrequency : null,
        recurringEndDate: isRecurring && recurringEndDate ? recurringEndDate : null,
        recurringNextDate: isRecurring && form.poDate ? form.poDate : null,
      };
      const { data } = await purchaseOrdersAPI.create(payload);
      navigate(`/purchase-orders/${data.poId}`);
    } catch (e) {
      setServerError(e.response?.data?.message || 'Failed to create purchase order');
    } finally {
      setLoading(false);
    }
  };

  const inputCls = (key) =>
    `border rounded-lg px-3 py-2 text-sm w-full ${fieldError[key] ? 'border-red-400 bg-red-50' : 'border-gray-300'}`;

  return (
    <div className="p-6 max-w-4xl">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate('/purchase-orders')} className="text-gray-400 hover:text-gray-600 text-sm">← POs</button>
        <h1 className="text-2xl font-bold text-gray-900">New Purchase Order</h1>
      </div>

      {serverError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{serverError}</div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Header fields */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">PO Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">PO Number <span className="text-red-500">*</span></label>
              <div className="flex gap-2">
                <input
                  className={`${inputCls('poNumber')} flex-1 font-mono`}
                  value={form.poNumber}
                  onChange={e => setField('poNumber', e.target.value)}
                  placeholder="e.g. PO-2026-05-0001"
                />
                <button
                  type="button"
                  onClick={fetchNextPoNumber}
                  disabled={poNumberLoading}
                  title="Suggest next PO number"
                  className="px-3 py-2 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-500 whitespace-nowrap disabled:opacity-50"
                >
                  {poNumberLoading ? '…' : '⟳ Suggest'}
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1">Auto-generated — you can edit this freely</p>
              {fieldError.poNumber && <p className="text-xs text-red-600 mt-1">{fieldError.poNumber}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">PO Type</label>
              <select className={inputCls('poType')} value={form.poType} onChange={e => setField('poType', e.target.value)}>
                <option value="standard">Standard</option>
                <option value="blanket">Blanket (revolving ceiling)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vendor <span className="text-red-500">*</span></label>
              <select className={inputCls('vendorId')} value={form.vendorId} onChange={e => setField('vendorId', e.target.value)}>
                <option value="">Select vendor…</option>
                {vendors.map(v => <option key={v.VendorID} value={v.VendorID}>{v.VendorName}</option>)}
              </select>
              {fieldError.vendorId && <p className="text-xs text-red-600 mt-1">{fieldError.vendorId}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
              <select className={inputCls('departmentId')} value={form.departmentId} onChange={e => setField('departmentId', e.target.value)}>
                <option value="">Select department…</option>
                {departments.map(d => <option key={d.DepartmentID} value={d.DepartmentID}>{d.DepartmentName || d.Name}</option>)}
              </select>
              {fieldError.departmentId && <p className="text-xs text-red-600 mt-1">{fieldError.departmentId}</p>}
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <input className={inputCls('description')} value={form.description} onChange={e => setField('description', e.target.value)} placeholder="Brief description…" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
              <select className={inputCls('currency')} value={form.currency} onChange={e => setField('currency', e.target.value)}>
                {['GBP','EUR','USD','CAD','AUD','CHF','NOK','SEK','DKK'].map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">PO Date</label>
              <input type="date" className={inputCls('poDate')} value={form.poDate} onChange={e => setField('poDate', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date</label>
              <input type="date" className={inputCls('expiryDate')} value={form.expiryDate} onChange={e => setField('expiryDate', e.target.value)} />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea rows={2} className={inputCls('notes')} value={form.notes} onChange={e => setField('notes', e.target.value)} placeholder="Internal notes…" />
            </div>
          </div>
        </div>

        {/* Line Items */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Line Items</h2>
            <button type="button" onClick={addLine} className="text-sm text-indigo-600 hover:text-indigo-800 font-medium">+ Add line</button>
          </div>
          <div className="space-y-3">
            {lines.map((line, i) => {
              const lineBudgetLines = budgetLines.filter(b => !line.glAccountId || b.GLAccountID === line.glAccountId);
              return (
                <div key={i} className="border border-gray-100 rounded-lg p-3 space-y-2 bg-gray-50/50">
                  {/* Row 1: Description, Qty, Unit Price, Category, Total + Remove */}
                  <div className="grid grid-cols-12 gap-2 items-start">
                    <div className="col-span-4">
                      {i === 0 && <label className="block text-xs text-gray-400 mb-1">Description</label>}
                      <input className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full bg-white" value={line.description} onChange={e => setLine(i, 'description', e.target.value)} placeholder="Item description" />
                    </div>
                    <div className="col-span-1">
                      {i === 0 && <label className="block text-xs text-gray-400 mb-1">Qty</label>}
                      <input type="number" min="0" step="any" className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full bg-white" value={line.quantity} onChange={e => setLine(i, 'quantity', e.target.value)} />
                    </div>
                    <div className="col-span-2">
                      {i === 0 && <label className="block text-xs text-gray-400 mb-1">Unit Price</label>}
                      <input type="number" min="0" step="0.01" className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full bg-white" value={line.unitPrice} onChange={e => setLine(i, 'unitPrice', e.target.value)} placeholder="0.00" />
                    </div>
                    <div className="col-span-2">
                      {i === 0 && <label className="block text-xs text-gray-400 mb-1">Category</label>}
                      <select className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full bg-white" value={line.itemCategory} onChange={e => setLine(i, 'itemCategory', e.target.value)}>
                        <option value="">—</option>
                        <option value="Stockable">Stockable</option>
                        <option value="Non-stockable">Non-stockable</option>
                        <option value="Service">Service</option>
                      </select>
                    </div>
                    <div className="col-span-3">
                      {i === 0 && <label className="block text-xs text-gray-400 mb-1 opacity-0">Total</label>}
                      <div className="flex items-center justify-between px-1 py-2">
                        <span className="text-xs font-medium text-gray-700">
                          {form.currency} {((parseFloat(line.quantity) || 0) * (parseFloat(line.unitPrice) || 0)).toFixed(2)}
                        </span>
                        {lines.length > 1 && (
                          <button type="button" onClick={() => removeLine(i)} className="text-red-400 hover:text-red-600 text-xs ml-2">✕</button>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* Row 2: GL Account, Budget Line */}
                  <div className="grid grid-cols-12 gap-2 items-start">
                    <div className="col-span-6">
                      {i === 0 && <label className="block text-xs text-gray-400 mb-1">GL Account{form.departmentId ? ' (filtered by dept)' : ''}</label>}
                      <select className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full bg-white" value={line.glAccountId} onChange={e => handleLineGL(i, e.target.value)}>
                        <option value="">— Select GL account —</option>
                        {availableGlAccounts.map(g => (
                          <option key={g.GLAccountID} value={g.GLAccountID}>{g.AccountCode} – {g.AccountName}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-span-6">
                      {i === 0 && <label className="block text-xs text-gray-400 mb-1">Budget Line{line.glAccountId ? ' (filtered by GL)' : ''}</label>}
                      <select className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full bg-white" value={line.budgetLineId} onChange={e => setLine(i, 'budgetLineId', e.target.value)}>
                        <option value="">—</option>
                        {lineBudgetLines.map(b => (
                          <option key={b.BudgetLineID} value={b.BudgetLineID}>{b.AccountCode ? `${b.AccountCode} – ` : ''}{b.Description} ({b.BudgetName})</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Total */}
          <div className="mt-4 pt-3 border-t border-gray-100 flex justify-end">
            <div className="text-right">
              <p className="text-sm text-gray-500">Total</p>
              <p className="text-xl font-bold text-gray-900">
                {new Intl.NumberFormat('en-GB', { style: 'currency', currency: form.currency }).format(totalAmount)}
              </p>
            </div>
          </div>
        </div>

        {/* Scheduling */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Scheduling &amp; Delivery</h2>
          <div className="space-y-4">
            {/* Auto-send toggle */}
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={autoSendOnApproval} onChange={e => setAutoSendOnApproval(e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-indigo-600" />
              <div>
                <p className="font-medium text-gray-800 text-sm">Auto-send to vendor on approval</p>
                <p className="text-xs text-gray-500">The PO PDF will be emailed to the vendor automatically when fully approved.</p>
              </div>
            </label>
            {/* CC field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">CC (optional)</label>
              <input
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full"
                value={vendorEmailCC}
                onChange={e => setVendorEmailCC(e.target.value)}
                placeholder="e.g. finance@company.com, manager@company.com"
              />
              <p className="text-xs text-gray-400 mt-1">Comma-separated list of CC email addresses.</p>
            </div>
            {/* Recurring toggle */}
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={isRecurring} onChange={e => setIsRecurring(e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-indigo-600" />
              <div>
                <p className="font-medium text-gray-800 text-sm">Recurring PO</p>
                <p className="text-xs text-gray-500">Mark as recurring to manually generate the next PO from the detail page.</p>
              </div>
            </label>
            {isRecurring && (
              <div className="ml-7 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
                  <select
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full"
                    value={recurringFrequency}
                    onChange={e => setRecurringFrequency(e.target.value)}
                  >
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="end_of_month">End of Month</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date (optional)</label>
                  <input
                    type="date"
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full"
                    value={recurringEndDate}
                    onChange={e => setRecurringEndDate(e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Save options */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={submitForApproval} onChange={e => setSubmitForApproval(e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-indigo-600" />
            <div>
              <p className="font-medium text-gray-800 text-sm">Submit for approval immediately</p>
              <p className="text-xs text-gray-500">The PO will be sent to department approvers straight away. Otherwise it saves as Draft.</p>
            </div>
          </label>
        </div>

        {/* Footer buttons */}
        <div className="flex gap-3 justify-end">
          <button type="button" onClick={() => navigate('/purchase-orders')} className="px-5 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
          <button type="submit" disabled={loading} className="px-6 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium">
            {loading ? 'Saving…' : submitForApproval ? 'Create & Submit' : 'Save as Draft'}
          </button>
        </div>
      </form>
    </div>
  );
}
